import { ArrowBack, ArrowForward } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { PatientContext } from "../../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../../contexts/ProjectContext.tsx";
import { projectsPrefix } from "../../../services/ProjectsServices.ts";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { getDiffDaysMessage } from "../../../utils/main.ts";
import CloseArrowTooltip from "../../CloseArrowTooltip.tsx";
import NoteText from "./NoteText.tsx";
import DwvViewer from "./dicom/DwvViewer.tsx";

export default function DocumentModal({
  document,
  categorize,
  closeModal,
  visitInfo,
  isFirst,
  isLast,
  goPrevDocument,
  goNextDocument,
}: {
  document: ESDoc;
  categorize: (e?: React.MouseEvent<HTMLElement>) => void;
  closeModal: () => void;
  visitInfo?: {
    visitId: string;
    encounterNumber: number;
    visitNameText?: string;
  };
  isFirst: boolean;
  isLast: boolean;
  goPrevDocument: () => void;
  goNextDocument: () => void;
}) {
  const dicomButtonAnchorRef = React.useRef<HTMLDivElement>(null);

  const {
    id: projectId,
    highlightDate,
    isLocked,
    radiologyImagesEnabled,
  } = useContext(ProjectContext) || {};
  const { id, patientNumber } = useContext(PatientContext) || {};
  const [dicomModal, setDicomModal] = useState(false);
  const [dicomNote, setDicomNote] = useState<ESDoc | null>(null);
  const [radiologyIndex, setRadiologyIndex] = React.useState(0);
  const [dicomButtonOpen, setDicomButtonOpen] = React.useState(false);

  const {
    data: fullDocument,
    fetch: fetchDocument,
    isLoading: isDocumentLoading,
    error: fetchDocumentError,
    reset: resetDocument,
  } = useFetch<ESDoc, undefined, Note>();

  const radiologyImageInfo = useMemo(() => {
    if (fullDocument && fullDocument.radiologyImageInfo) {
      return fullDocument.radiologyImageInfo.filter((dicomImage) => {
        return !!dicomImage.radiologyImageAvailable;
      });
    }

    return [];
  }, [fullDocument]);

  const selectedRadiologyImage = radiologyImageInfo[radiologyIndex];

  function handleDocumentNavigate(goToDocument: () => void) {
    resetDocument();
    setDicomButtonOpen(false);
    setRadiologyIndex(0);
    goToDocument();
  }

  function handleDicomModal(note: ESDoc) {
    setDicomModal(true);
    setDicomNote(note);
  }

  function closeDicomModal() {
    setDicomModal(false);
  }

  function handleButtonToggle() {
    setDicomButtonOpen((prevOpen) => !prevOpen);
  }

  function handleButtonClose(event: Event) {
    if (
      event.target instanceof HTMLElement &&
      dicomButtonAnchorRef.current &&
      dicomButtonAnchorRef.current.contains(event.target)
    ) {
      return;
    }

    setDicomButtonOpen(false);
  }

  function handleMenuItemClick(
    _: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) {
    setRadiologyIndex(index);
    setDicomButtonOpen(false);
  }

  useEffect(() => {
    if (!fullDocument && !isDocumentLoading && !fetchDocumentError) {
      void fetchDocument(
        `${projectsPrefix}/${projectId}/patients/${id}/document/${document.noteType}/${document.documentId}`,
      );
    }
  }, [
    document.documentId,
    document.noteType,
    fetchDocument,
    fetchDocumentError,
    fullDocument,
    id,
    isDocumentLoading,
    projectId,
  ]);

  useEffect(() => {
    if (fetchDocumentError) {
      console.error(`Error: ${fetchDocumentError}`);
    }
  }, [fetchDocumentError]);

  const dicomButtonText = useMemo(() => {
    if (radiologyImageInfo.length === 0) {
      return "No DICOM Images Available Right Now";
    }
    const fullText = `View ${selectedRadiologyImage.studyDescription ?? ""}${
      selectedRadiologyImage.seriesDescription
        ? " - " + selectedRadiologyImage.seriesDescription
        : ""
    }`;
    return fullText;
  }, [radiologyImageInfo, selectedRadiologyImage]);

  return (
    <>
      <Dialog
        open={true}
        onClose={closeModal}
        maxWidth={"md"}
        fullWidth
        slotProps={{
          paper: { sx: { height: "100vh" } },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor:
              highlightDate && new Date(document.loadedAt) >= highlightDate
                ? "highlight.main"
                : undefined,
          }}
        >
          <IconButton
            onClick={() => handleDocumentNavigate(goPrevDocument)}
            sx={{ "&:hover": { bgcolor: "action.hover" } }}
            disabled={isFirst}
          >
            <ArrowBack />
          </IconButton>
          {document.noteTitle}
          <IconButton
            onClick={() => handleDocumentNavigate(goNextDocument)}
            sx={{ "&:hover": { bgcolor: "action.hover" } }}
            disabled={isLast}
          >
            <ArrowForward />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Stack direction="column" paddingTop={1} spacing={1.5}>
            <Stack direction="row" spacing={1.5}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Pre/Post Index:
              </Typography>
              <Typography variant="body2">
                {getDiffDaysMessage(document.diffDays)}
              </Typography>
            </Stack>
            {isDocumentLoading || !fullDocument ? (
              <>
                <Typography variant="h6">Loading Full Document</Typography>
                <Skeleton
                  animation={"wave"}
                  variant="rounded"
                  height={"100%"}
                />
              </>
            ) : (
              <NoteText text={fullDocument.noteText ?? ""} variant={"body2"} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {radiologyImagesEnabled &&
            fullDocument &&
            radiologyImageInfo.length > 0 &&
            visitInfo && (
              <span style={{ marginRight: "auto", marginLeft: "1em" }}>
                <ButtonGroup
                  variant="outlined"
                  ref={dicomButtonAnchorRef}
                  size="small"
                  className={"categorize-button view-dicom-button"}
                >
                  <Button onClick={() => handleDicomModal(fullDocument)}>
                    {dicomButtonText.length > 100 ? (
                      <Tooltip title={dicomButtonText}>
                        <span>{dicomButtonText.slice(0, 100) + "..."}</span>
                      </Tooltip>
                    ) : (
                      <span>{dicomButtonText}</span>
                    )}
                  </Button>
                  {radiologyImageInfo.length > 1 && (
                    <Button size="small" onClick={handleButtonToggle}>
                      <ArrowDropDownIcon />
                    </Button>
                  )}
                </ButtonGroup>
                <Popper
                  sx={{ zIndex: 1 }}
                  open={dicomButtonOpen}
                  anchorEl={dicomButtonAnchorRef.current}
                  role={undefined}
                  transition
                  disablePortal
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom"
                            ? "center top"
                            : "center bottom",
                      }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={handleButtonClose}>
                          <MenuList id="split-button-menu" autoFocusItem>
                            {radiologyImageInfo.map((option, index) => {
                              const dicomText = `View ${option.studyDescription ?? ""}${
                                option.seriesDescription
                                  ? " - " + option.seriesDescription
                                  : ""
                              }`;
                              return (
                                <MenuItem
                                  key={index}
                                  selected={index === radiologyIndex}
                                  onClick={(event) => {
                                    handleMenuItemClick(event, index);
                                    handleDicomModal(fullDocument);
                                  }}
                                >
                                  {dicomText.length > 100 ? (
                                    <Tooltip title={dicomText}>
                                      <span>
                                        {dicomText.slice(0, 100) + "..."}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span>{dicomText}</span>
                                  )}
                                </MenuItem>
                              );
                            })}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </span>
            )}
          <Button size="small" variant="outlined" onClick={closeModal}>
            Close
          </Button>
          <CloseArrowTooltip title={isLocked ? "This project is locked." : ""}>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={isLocked}
                onClick={categorize}
              >
                Categorize Patient
              </Button>
            </span>
          </CloseArrowTooltip>
        </DialogActions>
      </Dialog>
      {dicomModal && dicomNote && selectedRadiologyImage && visitInfo && (
        <DwvViewer
          open={dicomModal}
          onClose={closeDicomModal}
          dicomNoteData={dicomNote}
          radiologyImageInfo={selectedRadiologyImage}
          categorize={categorize}
          patientNumber={patientNumber!}
          patientId={id!}
          visitId={visitInfo.visitId}
          visitNumber={visitInfo.encounterNumber}
          visitTitle={visitInfo.visitNameText ?? ""}
        />
      )}
    </>
  );
}
